/* Registration (rego) lookup using PlateAPI: https://api.plateapi.com.au/api/v1/lookup?plate=ABC123&state=VIC
   The API key is sent in the X-API-Key header. It is asked for once and kept in this browser only.
   For production, call PlateAPI from your server instead (set REACT_APP_PLATE_PROXY) so the key is never exposed. */
const KEY_STORE = 'plateapi_key';
const BASE = 'https://api.plateapi.com.au/api/v1/lookup';
const PROXY = process.env.REACT_APP_PLATE_PROXY || '';

export const getPlateKey = () => { try { return window.localStorage.getItem(KEY_STORE) || ''; } catch (e) { return ''; } };
export const setPlateKey = (k) => { try { if (k) window.localStorage.setItem(KEY_STORE, k.trim()); else window.localStorage.removeItem(KEY_STORE); } catch (e) { /* ignore */ } };
export const usesProxy = () => !!PROXY;

export const cleanPlate = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
export const validPlate = (v) => /^[A-Z0-9]{2,8}$/.test(cleanPlate(v));

// Sample answer from the PlateAPI documentation for the sandbox plate TEST123 (used only if the network call cannot be made).
const SANDBOX = { success: true, sandbox: true, match: 'exact', alternatives: [], vehicle: { year_range: '2015-2020', lowest_year: 2015, highest_year: 2020, make: 'TOYOTA', model: 'HILUX', description: '15~20 TOYOTA HILUX SR 2.8L DIESEL', body: 'UTE', engine: '2.8L' } };

export class PlateError extends Error { constructor(message, code) { super(message); this.code = code; } }

export const lookupPlate = async (plateRaw, state) => {
  const plate = cleanPlate(plateRaw);
  const key = getPlateKey();
  if (!PROXY && !key) throw new PlateError('An API key is needed to look up a registration.', 'nokey');
  const url = `${PROXY || BASE}?plate=${encodeURIComponent(plate)}&state=${encodeURIComponent(state)}`;
  let res;
  try {
    res = await fetch(url, { headers: PROXY ? {} : { 'X-API-Key': key, Accept: 'application/json' } });
  } catch (e) {
    if (plate === 'TEST123') return { ...SANDBOX, offline: true };
    throw new PlateError('We could not reach the registration lookup. Check your connection and try again.', 'network');
  }
  let data = null; try { data = await res.json(); } catch (e) { /* not json */ }
  if (res.status === 401 || res.status === 403) throw new PlateError('That API key was not accepted. Check it and try again.', 'auth');
  if (res.status === 429) throw new PlateError('Too many lookups right now. Please wait a moment and try again.', 'rate');
  if (!res.ok || !data || data.success === false) throw new PlateError((data && (data.error || data.message)) || 'No vehicle was found for that registration and state.', 'notfound');
  return data;
};

/** shape used across the storefront */
export const toVehicle = (data, plate, state) => {
  const v = data.vehicle || {};
  return { plate: cleanPlate(plate), state, make: String(v.make || '').toUpperCase(), model: String(v.model || '').toUpperCase(), year: v.year || null, from: v.lowest_year || v.year || null, to: v.highest_year || v.year || null, yearRange: v.year_range || '', description: v.description || `${v.make || ''} ${v.model || ''}`.trim(), body: v.body || '', engine: v.engine || '', alternatives: Array.isArray(data.alternatives) ? data.alternatives : [], sandbox: !!data.sandbox };
};

const title = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
export const vehicleLabel = (v) => (v ? `${v.yearRange ? `${v.yearRange} ` : v.year ? `${v.year} ` : ''}${title(v.make)} ${title(v.model)}`.trim() : '');

/** true = fits, false = does not, null = universal / no fitment data */
export const fitsVehicle = (product, v) => {
  const fits = product && (product.fits || (product.raw && product.raw.fits));
  if (!v) return null;
  if (!fits || !fits.length) return null;
  return fits.some((f) => f.make === v.make && f.model === v.model && (!v.from || (v.from <= f.to && (v.to || v.from) >= f.from)));
};
